// src/components/Control/Operator/status.ts
// Сводное состояние робота для шапки и строки статуса: из сессии Zenoh и телеметрии.
import { useTick } from '../../../hooks/useTick';
import { useTelemetryState, useZenohInfo, useDriveState } from '../robot/RobotContext';
import { MODE_NAMES, STALE, fresh } from '../robot/telemetry';
import { isStale } from '../../../types/features';

export type Level = 'ok' | 'warn' | 'error' | 'off';

export interface Conn {
  level: Level;
  text: string;       // коротко для шапки
  detail: string;     // объяснение
}

export function useRobotStatus() {
  useTick(1000);
  const zstate = useZenohInfo(z => z.state);
  const t = useTelemetryState(s => ({
    status: s.status, statusAt: s.statusAt, mission: s.mission, missionAt: s.missionAt,
    poseAt: s.poseAt, v: s.v, scanAt: s.scanAt, scanHz: s.scanHz, people: s.people, personAt: s.personAt,
    obstacle: s.obstacle, obstacleAt: s.obstacleAt, audioAt: s.audio?.timestamp, nanny: !!s.audio?.nanny?.enabled,
    visionAt: s.vision?.timestamp, clients: s.clients, chassis: s.chassis,
  }));
  const stoppedAt = useDriveState(d => d.stoppedAt);
  const moving = useDriveState(d => d.moving);

  const statusFresh = fresh(t.statusAt, STALE.status);
  const st = statusFresh ? t.status : null;

  let conn: Conn;
  if (zstate === 'connecting') conn = { level: 'warn', text: 'Подключение…', detail: 'Открываю связь с сервером (Zenoh).' };
  else if (zstate === 'reconnecting') conn = { level: 'warn', text: 'Переподключение…', detail: 'Связь с сервером пропала, повторяю каждые 5 с.' };
  else if (zstate === 'idle') conn = { level: 'off', text: 'Отключено', detail: 'Zenoh отключён вручную. Подключить — в «Настройки → Zenoh».' };
  else if (!st) conn = { level: 'error', text: 'Робот недоступен', detail: t.statusAt ? 'Робот перестал присылать статус.' : 'Робот ещё не прислал статус (не включён или не в сети?).' };
  else if (st.status === 'offline') conn = { level: 'error', text: 'Робот офлайн', detail: `Связь Pi с контроллером: ${st.link ?? 'нет'}.` };
  else if (st.chassis === false) conn = { level: 'ok', text: 'На связи', detail: 'Pi без шасси: камера, микрофон, динамик.' };
  else conn = { level: 'ok', text: 'На связи', detail: `Связь с контроллером: ${st.link ?? 'есть'}.` };

  const mission = fresh(t.missionAt, STALE.mission) ? t.mission : null;
  const mode = mission?.mode ?? null;
  const autonomous = !!mode && mode !== 'idle';

  const people = fresh(t.personAt, STALE.person) ? t.people : [];
  const lidarOk = fresh(t.scanAt, STALE.scan);
  const speed = st?.speed ?? (fresh(t.poseAt, STALE.pose) ? t.v : null);
  const obstacle = fresh(t.obstacleAt, STALE.obstacle) && t.obstacle?.valid !== false && t.obstacle?.enabled !== false ? t.obstacle : null;

  // Режим для шапки: STOP, ручное, автономия
  let modeText = 'Ожидание';
  let modeLevel: Level = 'off';
  if (Date.now() - stoppedAt < 3000) {
    modeText = 'Остановлен';
    modeLevel = 'error';
  } else if (moving) {
    modeText = 'Ручное управление';
    modeLevel = 'ok';
  } else if (autonomous) {
    modeText = `Автономия: ${MODE_NAMES[mode!] ?? mode}`;
    modeLevel = 'warn';
  } else if (!mission && st) {
    modeText = 'Автономия не на связи';
  }

  const power = st?.power ?? null;
  const asleep = !!power?.asleep;

  return {
    zstate,
    chassis: t.chassis,
    power,
    asleep,
    clients: t.clients,
    conn,
    status: st,
    statusAt: t.statusAt,
    battery: st?.battery ?? null,
    charging: !!st?.charging,
    voltage: st?.voltage ?? null,
    mission,
    mode,
    autonomous,
    modeText,
    modeLevel,
    speed,
    people,
    lidarOk,
    scanAt: t.scanAt,
    scanHz: t.scanHz,
    cam1: st ? !!st.camera : null,
    cam2: st?.cam2 ?? null,
    hasMap: !!st?.map,
    obstacle,
    nanny: t.nanny && !isStale(t.audioAt),
    audioOnline: !isStale(t.audioAt),
    visionOnline: !isStale(t.visionAt),
  };
}

export type RobotStatus = ReturnType<typeof useRobotStatus>;

export function ago(ms: number): string {
  if (!ms) return '—';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s} с назад`;
  if (s < 3600) return `${Math.round(s / 60)} мин назад`;
  return new Date(ms).toLocaleTimeString();
}
